import { Button } from '@mui/joy';

import { Dispatch, SetStateAction, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Dialog, DialogContent } from '@/components/Dialog/Dialog';
import useToast from '@/components/Toast/useToast';
import {
  useCloneCustomChartsSectionMutation,
  useOrganizationId,
} from '@/hooks/useSwr';
import { appDashboardsIndexPath, appOrganizationPath } from '@/utils/constants';

type CloneDashboardDialogProps = {
  children: (setOpen: Dispatch<SetStateAction<boolean>>) => React.ReactNode;
  dashboardId?: string;
  sessionId?: string;
};

export function CloneDashboardDialog({
  dashboardId,
  sessionId,
  children,
}: CloneDashboardDialogProps) {
  const [open, setOpen] = useState(false);
  const { trigger, isMutating: isLoading } =
    useCloneCustomChartsSectionMutation();
  const { createToast } = useToast();
  const navigate = useNavigate();

  const orgId = useOrganizationId();

  const handleClone = async () => {
    try {
      let response;
      if (dashboardId) {
        response = await trigger({
          json: {
            section_id: dashboardId,
          },
        });
      } else if (sessionId) {
        response = await trigger({
          json: {
            session_id: sessionId,
          },
        });
      }
      setOpen(false);
      if (response.id) {
        navigate(
          `/${appOrganizationPath}/${orgId}/${appDashboardsIndexPath}/${response.id}`
        );
      }
    } catch (e) {
      createToast({
        title: 'Error cloning dashboard',
        type: 'error',
      });
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent showClose={true} title="Clone Dashboard">
          Are you sure you want to clone this dashboard?
          <div className="flex justify-end gap-2">
            <Button
              variant="outlined"
              color="neutral"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleClone} loading={isLoading}>
              Clone
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {children(setOpen)}
    </>
  );
}
